import {useCallback, useMemo, useState} from 'react';
import classNames from 'classnames';

import {HighlightPointType, HighlightType} from '../types';
import ImageContainer from './ImageContainer.client';
import {Link} from '@shopify/hydrogen';

interface HighlightBlockProps {
  highlight: HighlightType<HighlightPointType>;
}

export default function HighlightBlock({highlight}: HighlightBlockProps) {
  const [showAll, setShowAll] = useState(false);
  const toggleShow = useCallback(() => setShowAll((showAll) => !showAll), []);

  const showShowAllButton = useMemo(
    () => highlight.highlight_points?.length > 2,
    [highlight.highlight_points?.length],
  );

  return (
    <div key={highlight.title} className="highlight-area">
      <div className="highlight-area__image">
        <div>
          <HighlightLink to={highlight.url} className="text-content-no-a-style">
            <ImageContainer
              jpegSrc={highlight.image_jpeg}
              webpSrc={highlight.image_webp}
              widthInPx={800}
              heightInPx={800}
              alt={highlight.title}
            />
          </HighlightLink>
        </div>
      </div>
      <div className="highlight-area__contents">
        <h3 className="highlight-area__title title-2">
          <HighlightLink to={highlight.url} className="text-content-no-a-style">
            {highlight.title}
          </HighlightLink>
        </h3>
        <div className="font-13 c-accent secondary-font mt-10">
          {highlight.subtitle}
        </div>
        <div
          className={classNames('text-content q_a_mini', {
            'show-all': showAll,
          })}
        >
          <ul>
            {highlight.highlight_points.map((highlight) => (
              <li key={highlight.label}>
                <h6 className="title-4 question">{highlight.label}</h6>
                <div className="c-dark-gray answer">
                  {highlight.description}
                </div>
              </li>
            ))}
          </ul>
          {showShowAllButton && (
            <div className="qa_read-more js-qa-read-more">
              <i className="icon-plus-red-circle" />
              <span
                role="button"
                tabIndex={-1}
                onClick={toggleShow}
                className="read-more"
              >
                Read more
              </span>
              <span
                role="button"
                tabIndex={-1}
                onClick={toggleShow}
                className="read-less"
              >
                Show less
              </span>
            </div>
          )}
        </div>
        {highlight.button_text && highlight.url && (
          <HighlightLink
            to={highlight.url}
            className="button text-content-no-a-style"
          >
            {highlight.button_text}
          </HighlightLink>
        )}
      </div>
    </div>
  );
}

function HighlightLink({to, children, ...props}) {
  return to ? (
    <Link to={to} {...props}>
      {children}
    </Link>
  ) : (
    <>{children}</>
  );
}
